import {RecurrenceRule, RecurrenceWeekday} from './recurrence';

/**
 * A one-line, human-readable description of a repeat, e.g.
 *   "Repeats every week on Monday and Friday, 12 times"
 *   "Repeats every 2 months on the last Friday, until 30 Jun 2026"
 *
 * Built by hand rather than from a rule-to-RRULE-to-text library: there are three
 * frequencies and two monthly branches, and a tired reader can follow this top to bottom.
 */
export function describeRecurrence(rule: RecurrenceRule | null | undefined): string {
  if (!rule) {
    return '';
  }
  return `Repeats ${cadence(rule)}${pattern(rule)}${ends(rule)}`;
}

/** "every week" / "every 2 weeks" */
function cadence(rule: RecurrenceRule): string {
  const unit = rule.frequency === 'Daily' ? 'day' : rule.frequency === 'Weekly' ? 'week' : 'month';
  return rule.interval > 1 ? `every ${rule.interval} ${unit}s` : `every ${unit}`;
}

/** " on Monday and Friday" / " on the last Friday" / " on day 15" — empty for daily. */
function pattern(rule: RecurrenceRule): string {
  if (rule.frequency === 'Weekly') {
    // An empty list means "the appointment's own weekday", which the dates already show.
    return rule.weeklyDays.length ? ` on ${joinWords(rule.weeklyDays)}` : '';
  }
  if (rule.frequency === 'Monthly') {
    if (rule.monthlyMode === 'ByDayOfMonth' && rule.monthlyDayOfMonth) {
      return ` on day ${rule.monthlyDayOfMonth}`;
    }
    if (rule.monthlyMode === 'ByNthWeekday' && rule.monthlyNth && rule.monthlyWeekday) {
      return ` on the ${rule.monthlyNth.toLowerCase()} ${rule.monthlyWeekday}`;
    }
  }
  return '';
}

/** ", 12 times" / ", until 30 Jun 2026" — one or the other; the server requires exactly one. */
function ends(rule: RecurrenceRule): string {
  if (rule.endsAfterCount) {
    return `, ${rule.endsAfterCount} ${rule.endsAfterCount === 1 ? 'time' : 'times'}`;
  }
  if (rule.endsOnDate) {
    return `, until ${formatDate(rule.endsOnDate)}`;
  }
  return '';
}

function joinWords(days: RecurrenceWeekday[]): string {
  if (days.length === 1) {
    return days[0];
  }
  return `${days.slice(0, -1).join(', ')} and ${days[days.length - 1]}`;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-ZA', {day: 'numeric', month: 'short', year: 'numeric'});
}
